const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Immutable calendar date with no time component.
 *
 * Internal representation is the canonical "YYYY-MM-DD" string, so instances
 * serialize cleanly to JSON / LocalStorage and compare correctly with `===`
 * on `.value` (and lexicographically with `<` / `>`).
 *
 * All arithmetic is done in UTC at noon to avoid DST and timezone date-shift
 * bugs that occur when treating an epoch as a calendar date.
 */
export class DateOnly {
  /** Canonical "YYYY-MM-DD" representation. */
  readonly value: string;

  private constructor(value: string) {
    this.value = value;
  }

  /** Today's date in the user's local timezone. */
  static today(): DateOnly {
    return DateOnly.fromLocalDate(new Date());
  }

  static fromString(value: string): DateOnly {
    if (!DATE_ONLY_RE.test(value)) {
      throw new Error(`DateOnly.fromString: expected "YYYY-MM-DD", got "${value}"`);
    }

    return new DateOnly(value);
  }

  /**
   * Accepts a "YYYY-MM-DD" string, an epoch-seconds number, or null/undefined.
   * Empty / null / undefined falls back to today. Numbers (or numeric strings)
   * are interpreted as Unix epoch seconds.
   */
  static resolve(value: string | number | null | undefined): DateOnly {
    if (value === null || value === undefined || value === '') {
      return DateOnly.today();
    }

    const s = String(value);
    if (DATE_ONLY_RE.test(s)) {
      return new DateOnly(s);
    }

    return DateOnly.fromEpochSeconds(Number(s));
  }

  static fromEpochSeconds(epoch: number): DateOnly {
    return new DateOnly(new Date(epoch * 1000).toISOString().slice(0, 10));
  }

  /** Build from the *local* calendar date of a Date object. */
  static fromLocalDate(date: Date): DateOnly {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return new DateOnly(`${year}-${month}-${day}`);
  }

  /** Build from the *UTC* calendar date of a Date object. */
  static fromUTCDate(date: Date): DateOnly {
    return new DateOnly(date.toISOString().slice(0, 10));
  }

  get year(): number {
    return Number(this.value.slice(0, 4));
  }

  get month(): number {
    return Number(this.value.slice(5, 7));
  }

  get day(): number {
    return Number(this.value.slice(8, 10));
  }

  /** 0=Sun … 6=Sat, computed in UTC so there is no timezone date-shift. */
  get weekday(): number {
    return this.toUtcNoonDate().getUTCDay();
  }

  get weekdayShort(): string {
    return this.toUtcNoonDate().toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  }

  get monthDayShort(): string {
    return this.toUtcNoonDate().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }

  get isWeekend(): boolean {
    const wd = this.weekday;
    return wd === 0 || wd === 6;
  }

  /** "st", "nd", "rd", or "th" for the day-of-month. */
  get ordinalSuffix(): string {
    const d = this.day;
    if (d > 3 && d < 21) {
      return 'th';
    }

    switch (d % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  toEpochSeconds(): number {
    return Date.UTC(this.year, this.month - 1, this.day, 12, 0, 0) / 1000;
  }

  toUtcNoonDate(): Date {
    return new Date(this.toEpochSeconds() * 1000);
  }

  toString(): string {
    return this.value;
  }

  toJSON(): string {
    return this.value;
  }

  valueOf(): string {
    return this.value;
  }

  addDays(days: number): DateOnly {
    return new DateOnly(new Date(Date.UTC(this.year, this.month - 1, this.day + days)).toISOString().slice(0, 10));
  }

  diffDays(other: DateOnly): number {
    return Math.round((this.toEpochSeconds() - other.toEpochSeconds()) / 86400);
  }

  mondayOfWeek(): DateOnly {
    const jsDay = this.weekday;
    const isoDay = jsDay === 0 ? 7 : jsDay;
    return this.addDays(1 - isoDay);
  }

  equals(other: DateOnly): boolean {
    return this.value === other.value;
  }

  isBefore(other: DateOnly): boolean {
    return this.value < other.value;
  }

  isAfter(other: DateOnly): boolean {
    return this.value > other.value;
  }

  isSameOrBefore(other: DateOnly): boolean {
    return this.value <= other.value;
  }

  isSameOrAfter(other: DateOnly): boolean {
    return this.value >= other.value;
  }
}
